import React, {
  ComponentType,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { useDropzone } from 'react-dropzone';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { httpClient } from '../../providers/dataProvider';
import useConfig from '../../hooks/useConfig';
import { getCurrentUserId } from '../../helpers/auth';
import { handleError } from '../../helpers/error';
import ContractSignatureLaunchModal from '../../components/modals/ContractSignatureLaunchModal';
import PhoneConfirmationModal from '../../components/modals/PhoneConfirmationModal';
import {
  CONTRACT_SIGNATURE_MODE_CHANGED_EVENT,
  OPEN_CONTRACT_SIGNATURE_LAUNCH_EVENT,
  OpenContractSignatureLaunchDetail,
} from '../../constants/contractSignature';

const DEFAULT_MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 Mo
const DEFAULT_ACCEPTED_MIME_TYPE = 'application/pdf';

const useStyles = makeStyles({
  dropzone: {
    marginTop: '8px',
    border: '2px dashed #ccc',
    borderRadius: '4px',
    padding: '16px',
    textAlign: 'center',
    cursor: 'pointer',
    color: '#666',
    fontSize: '14px',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  dropzoneActive: {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
  },
  dropzoneDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  icon: {
    marginBottom: '4px',
  },
  signRow: {
    marginTop: '8px',
  },
  status: {
    marginTop: '8px',
    color: '#666',
    fontSize: '14px',
  },
  warning: {
    color: '#b26a00',
  },
});

type PdfDropzoneParams = {
  // Attachment kind sent to POST /attachments on upload.
  attachmentKind: string;
  // The record carrying the PDF, when it isn't the one displayed: e.g. the
  // purchase order's "Contrat" tab shows its contract (`record.contract`,
  // updated through /contracts).
  entity?: { path: string; resource: string };
  // The displayed record's resource, for generatePdfPath / pdfStatusPath:
  // react-admin's `resource` prop doesn't reach a field rendered in Sections
  recordResource?: string;
  // Exports the generated contract (Google Doc, `google_doc_url` on the
  // displayed record) as the PDF: PUT /<resource>/<id>/<generatePdfPath>
  generatePdfPath?: string;
  // Tells whether the PDF predates the latest change to the generated
  // contract (Google Doc): GET /<resource>/<id>/<pdfStatusPath>, asked here
  // only, the API calling Google Drive for it
  pdfStatusPath?: string;
  // What the field shows: 'document' (the PDF, its generation and upload),
  // 'signature' (its e-signature status and buttons), or both ('all', the
  // default) -- split on the purchase order's "Contrat" tab so that the
  // "Type de signature" input sits between them, above the launch button.
  display?: 'all' | 'document' | 'signature';
  messages: {
    dropzoneLabel: string;
    uploading: string;
    invalidFormat: string;
    fileTooLarge: string;
    replaced: string;
    replaceFailed: string;
    // Shown when there's no entity (e.g. no contract yet for the order)
    noEntity?: string;
    // Shown as a window.confirm() prompt (interpolated with the record's
    // `expert_full_name`) before replacing a file whose signature is in
    // progress (`signature_launched`): its Yousign request is then canceled
    // and the signature launched again on the new file, notifying the expert
    // again. confirmExpertSigned once the expert signed, confirmSignatureLaunched
    // before.
    confirmExpertSigned?: string;
    confirmSignatureLaunched?: string;
    // Button shown to the contract signatory admin (`contract_signatory_id`)
    // once the PDF is uploaded: launches the Yousign signature request (the
    // expert, then them) once its recap is confirmed in a modal.
    launchLabel?: string;
    // Once launched: the expert is notified to sign (interpolated with `name`)
    launchSucceeded?: string;
    // Replaces it while the expert hasn't signed: notifies them again
    // ("Contract To Be Signed").
    resendLabel?: string;
    resendSucceeded?: string;
    // Button shown to the contract signatory admin once the expert has
    // signed (`expert_signed_awaiting_admin: true`): opens their Yousign link.
    signLabel?: string;
    // Signature status (awaitingAdmin is interpolated with `name`)
    awaitingExpert?: string;
    awaitingAdmin?: string;
    // Fallback when the API error has no translation
    signFailed?: string;
    // Modal shown before launching the signature, to check the admin's
    // phone number (Yousign creates their s2 signer with it at launch);
    // saved on their account.
    phoneModalTitle?: string;
    phoneModalDescription?: string;
    // Shown while the PDF is older than the latest change to the purchase
    // order's Google Doc (see `pdfStatusPath`)
    pdfOutdated?: string;
    generatePdfLabel?: string;
    generatePdfSucceeded?: string;
    // Shown under the disabled button until the contract is generated
    generatePdfUnavailable?: string;
  };
  maxFileSize?: number;
  acceptedMimeType?: string;
};

/**
 * Renders a download button for the record's current PDF attachment (if
 * any) plus a drag & drop zone to upload a replacement.
 */
function withPdfDropzone(
  Component: ComponentType<any>,
  params: Record<string, any> | undefined
): FunctionComponent {
  const {
    attachmentKind,
    entity,
    generatePdfPath,
    pdfStatusPath,
    recordResource: recordResourceParam,
    display = 'all',
    messages,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    acceptedMimeType = DEFAULT_ACCEPTED_MIME_TYPE,
  } = (params || {}) as PdfDropzoneParams;

  const Extension: FunctionComponent<Record<string, any>> = (props) => {
    const { record, source, resource } = props;
    const classes = useStyles();
    const notify = useNotify();
    const refresh = useRefresh();
    const t = useTranslate();
    const config = useConfig();
    const [uploading, setUploading] = useState(false);
    const [signing, setSigning] = useState(false);
    const [launchModalOpen, setLaunchModalOpen] = useState(false);
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);
    const [adminPhone, setAdminPhone] = useState<string>();
    // Mode just saved by the "Type de signature" input, ahead of the record
    const [launchSignatureMode, setLaunchSignatureMode] = useState<string>();
    const [pdfOutdated, setPdfOutdated] = useState(false);

    const target = entity ? get(record, entity.path) : record;
    // Something to sign: the PDF shown, uploaded or generated from the
    // purchase order's Google Doc ("Générer le PDF"), the one sent to Yousign
    const hasDocument = Boolean(target?.attachment);
    const targetResource = entity ? entity.resource : resource;
    const recordResource = recordResourceParam || resource;
    const targetId = target?.id;
    const isSignatory =
      Boolean(target?.contract_signatory_id) &&
      String(target?.contract_signatory_id) === String(getCurrentUserId());

    // Once "Automatique" is consented from the "Type de signature" input,
    // the launch recap opens (see withContractSignatureMode)
    const showDocument = display !== 'signature';
    const showSignature = display !== 'document';

    useEffect(() => {
      // The launch recap lives with the signature buttons
      if (!showSignature) return undefined;

      const openLaunch = (event: Event) => {
        const { contractId, signatureMode } = (
          event as CustomEvent<OpenContractSignatureLaunchDetail>
        ).detail;
        if (String(contractId) !== String(targetId)) return;
        if (!isSignatory || !hasDocument || target?.signature_launched) return;

        setLaunchSignatureMode(signatureMode);
        setLaunchModalOpen(true);
      };

      window.addEventListener(OPEN_CONTRACT_SIGNATURE_LAUNCH_EVENT, openLaunch);

      return () =>
        window.removeEventListener(
          OPEN_CONTRACT_SIGNATURE_LAUNCH_EVENT,
          openLaunch
        );
    }, [targetId, isSignatory, target, hasDocument, showSignature]);

    // The "Type de signature" chosen, saved with the purchase order or not:
    // shown in the launch recap and applied at launch
    useEffect(() => {
      if (!showSignature) return undefined;

      const changeMode = (event: Event) => {
        const { contractId, signatureMode } = (
          event as CustomEvent<OpenContractSignatureLaunchDetail>
        ).detail;
        if (String(contractId) === String(targetId)) {
          setLaunchSignatureMode(signatureMode);
        }
      };

      window.addEventListener(
        CONTRACT_SIGNATURE_MODE_CHANGED_EVENT,
        changeMode
      );

      return () =>
        window.removeEventListener(
          CONTRACT_SIGNATURE_MODE_CHANGED_EVENT,
          changeMode
        );
    }, [targetId, showSignature]);

    // The API answers its business errors with their key as `message`
    // (e.g. 'e_signature_procedure.contract.not_signatory'), translated
    // under flash.error, as in the front office.
    const notifyError = useCallback(
      (error: unknown, fallback?: string) => {
        const key = `flash.error.${get(error, 'body.message')}`;
        if (t(key) !== key) {
          notify(key, 'error');
        } else if (fallback) {
          notify(fallback, 'error');
        }
      },
      [notify, t]
    );

    // Asked before replacing the PDF (uploaded or generated) while its
    // signature is in progress; false when declined
    const confirmReplacement = useCallback(() => {
      const confirmation = target?.expert_signed_awaiting_admin
        ? messages.confirmExpertSigned
        : target?.signature_launched && messages.confirmSignatureLaunched;

      return (
        !confirmation ||
        window.confirm(
          t(confirmation, { expert_name: target?.expert_full_name })
        )
      );
    }, [t, target]);

    const generatePdf = useCallback(async () => {
      if (!generatePdfPath || !confirmReplacement()) return;

      setUploading(true);
      try {
        await httpClient(
          `${config.apiUrl}/${recordResource}/${record?.id}/${generatePdfPath}`,
          { method: 'PUT' }
        );
        if (messages.generatePdfSucceeded) {
          notify(messages.generatePdfSucceeded, 'success');
        }
        refresh();
      } catch (error) {
        handleError(error as Record<string, unknown>, notify, undefined, t);
      } finally {
        setUploading(false);
      }
    }, [
      config.apiUrl,
      recordResource,
      record?.id,
      confirmReplacement,
      notify,
      refresh,
      t,
    ]);

    const uploadFile = useCallback(
      async (file: File) => {
        setUploading(true);

        try {
          const { json } = await httpClient(`${config.apiUrl}/attachments`, {
            method: 'POST',
            body: JSON.stringify({
              name: file.name,
              mime_type: file.type,
              size: file.size,
              kind: attachmentKind,
              file_uuid: uuidv4(),
            }),
          });
          const { id, upload_url } = json as {
            id: string | number;
            upload_url: string;
          };

          await axios.put(upload_url, file, {
            headers: { 'Content-Type': file.type },
          });

          await httpClient(`${config.apiUrl}/${targetResource}/${targetId}`, {
            method: 'PUT',
            body: JSON.stringify({ attachment_id: id }),
          });

          notify(messages.replaced, 'success');
          refresh();
        } catch (error) {
          notifyError(error, messages.replaceFailed);
        } finally {
          setUploading(false);
        }
      },
      [config.apiUrl, targetResource, targetId, notify, notifyError, refresh]
    );

    const onDrop = useCallback(
      (acceptedFiles: File[], rejectedFiles: File[]) => {
        if (rejectedFiles.length > 0) {
          const [rejected] = rejectedFiles;
          notify(
            rejected.type !== acceptedMimeType
              ? t(messages.invalidFormat)
              : t(messages.fileTooLarge),
            'warning'
          );

          return;
        }

        const [file] = acceptedFiles;
        if (!file) return;

        if (!confirmReplacement()) return;

        uploadFile(file);
      },
      [notify, t, uploadFile, confirmReplacement]
    );

    const locked = Boolean(target?.fully_signed);
    const attachment = target?.attachment;

    // Only while it can still be generated again: launched, the PDF is sent.
    // Asked again once the record is refreshed (PDF or contract regenerated).
    const checkPdfStatus = Boolean(
      showDocument &&
        pdfStatusPath &&
        attachment?.id &&
        record?.google_doc_url &&
        !target?.signature_launched
    );
    useEffect(() => {
      if (!checkPdfStatus) {
        setPdfOutdated(false);

        return undefined;
      }

      let cancelled = false;
      httpClient(
        `${config.apiUrl}/${recordResource}/${record?.id}/${pdfStatusPath}`
      )
        .then(({ json }) => {
          if (!cancelled) {
            setPdfOutdated(
              Boolean(
                (json as { contract_pdf_outdated?: boolean })
                  .contract_pdf_outdated
              )
            );
          }
        })
        .catch(() => {
          // No warning: the PDF is shown as is
          if (!cancelled) setPdfOutdated(false);
        });

      return () => {
        cancelled = true;
      };
    }, [
      checkPdfStatus,
      config.apiUrl,
      recordResource,
      record?.id,
      record?.updated_at,
      attachment?.id,
      pdfStatusPath,
    ]);

    const openPhoneModal = useCallback(async () => {
      setPhoneModalOpen(true);

      try {
        const { json } = await httpClient(
          `${config.apiUrl}/admins/${getCurrentUserId()}`
        );
        setAdminPhone((json as { phone?: string }).phone || '');
      } catch {
        // The admin types their number in.
      }
    }, [config.apiUrl]);

    const updateAdminPhone = useCallback(
      async (phone: string) => {
        await httpClient(`${config.apiUrl}/admins/${getCurrentUserId()}`, {
          method: 'PUT',
          body: JSON.stringify({ phone }),
        });
        setAdminPhone(phone);
      },
      [config.apiUrl]
    );

    const launchSignature = useCallback(
      async (phone: string) => {
        setSigning(true);

        try {
          await httpClient(
            `${config.apiUrl}/${targetResource}/${targetId}/launch_signature`,
            {
              method: 'POST',
              body: JSON.stringify({
                phone,
                signature_mode: launchSignatureMode || target?.signature_mode,
              }),
            }
          );
          setPhoneModalOpen(false);
          if (messages.launchSucceeded) {
            notify(messages.launchSucceeded, 'success', {
              name: target?.expert_full_name,
            });
          }
          refresh();
        } catch (error) {
          notifyError(error, messages.signFailed);
        } finally {
          setSigning(false);
        }
      },
      [
        config.apiUrl,
        targetResource,
        targetId,
        launchSignatureMode,
        target,
        notify,
        notifyError,
        refresh,
      ]
    );

    const resendSignatureLink = useCallback(async () => {
      setSigning(true);

      try {
        await httpClient(
          `${config.apiUrl}/${targetResource}/${targetId}/resend_signature_link`,
          { method: 'POST' }
        );
        if (messages.resendSucceeded) {
          notify(messages.resendSucceeded, 'success');
        }
      } catch (error) {
        notifyError(error, messages.signFailed);
      } finally {
        setSigning(false);
      }
    }, [config.apiUrl, targetResource, targetId, notify, notifyError]);

    const signAsAdmin = useCallback(async () => {
      // Opened before the request so the browser doesn't block it as a
      // popup not triggered by the click.
      const signatureWindow = window.open('', '_blank');
      setSigning(true);

      try {
        const { json } = await httpClient(
          `${config.apiUrl}/${targetResource}/${targetId}/admin_signature`,
          { method: 'POST' }
        );
        const { signature_link } = json as { signature_link?: string };
        // Never navigate to an empty link (the page would become ".../null")
        if (!signature_link) throw new Error('signature_link missing');

        if (signatureWindow) {
          signatureWindow.location.href = signature_link;
        } else {
          window.location.href = signature_link;
        }
      } catch (error) {
        signatureWindow?.close();
        notifyError(error, messages.signFailed);
      } finally {
        setSigning(false);
      }
    }, [config.apiUrl, targetResource, targetId, notifyError]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: acceptedMimeType,
      multiple: false,
      maxSize: maxFileSize,
      disabled: uploading || locked,
      onDrop,
    });

    const buttons = useMemo(
      () =>
        attachment
          ? [
              {
                id: 'download',
                key: 'download',
                // Same as the signed contract's button (withAttachmentsFiles):
                // the API renders the PDF with public_url and name (see
                // ContractsController#render_include_for_show)
                href: attachment.public_url,
                label: attachment.name,
                icon: true,
                target: '_blank',
                rel: 'noopener',
              },
            ]
          : [],
      [attachment]
    );

    const mergedRecord = useMemo(
      () => ({ ...record, [source]: buttons }),
      [record, source, buttons]
    );

    if (!record || !source) return null;

    if (!target) {
      return showDocument && messages.noEntity ? (
        <div className={classes.status}>{t(messages.noEntity)}</div>
      ) : null;
    }

    if (locked) {
      return showDocument ? (
        <Component {...props} record={mergedRecord} />
      ) : null;
    }

    const awaitingExpert =
      target.signature_launched && !target.expert_signed_awaiting_admin;
    const canLaunch = isSignatory && hasDocument && !target.signature_launched;
    const canResend = isSignatory && awaitingExpert;
    // Replacing the PDF while its signature is in progress launches it
    // again: the contract signatory's only (the API's ContractPolicy#update?)
    const canReplace = !target.signature_launched || isSignatory;
    // In "Automatique" mode, Yousign signs for the admin (the API's
    // Contract#auto_sign_if_due!) -- unless it failed: they then sign by hand
    const canSign =
      isSignatory &&
      target.expert_signed_awaiting_admin &&
      (target.signature_mode !== 'automatic' ||
        target.automatic_signature_failed);

    // 1. The PDF to sign: shown, then generated from the contract or uploaded
    const documentPart = (
      <>
        <Component {...props} record={mergedRecord} />
        {pdfOutdated && messages.pdfOutdated && (
          <div className={`${classes.status} ${classes.warning}`}>
            {t(messages.pdfOutdated)}
          </div>
        )}
        {canReplace && generatePdfPath && messages.generatePdfLabel && (
          <div className={classes.signRow}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={uploading || !record.google_doc_url}
              onClick={generatePdf}
            >
              {t(messages.generatePdfLabel)}
            </Button>
            {!record.google_doc_url && messages.generatePdfUnavailable && (
              <div className={classes.status}>
                {t(messages.generatePdfUnavailable)}
              </div>
            )}
          </div>
        )}
        {canReplace && (
          <div
            {...getRootProps()}
            className={`${classes.dropzone} ${
              isDragActive ? classes.dropzoneActive : ''
            } ${uploading ? classes.dropzoneDisabled : ''}`.trim()}
          >
            <input {...getInputProps()} />
            {uploading ? (
              <CircularProgress size={24} className={classes.icon} />
            ) : (
              <CloudUploadIcon className={classes.icon} fontSize="large" />
            )}
            <div>
              {uploading ? t(messages.uploading) : t(messages.dropzoneLabel)}
            </div>
          </div>
        )}
      </>
    );

    // 2. Its e-signature: where it stands, then launch / resend / sign
    const signaturePart = (
      <>
        {awaitingExpert && messages.awaitingExpert && (
          <div className={classes.status}>
            {t(messages.awaitingExpert, { name: target.expert_full_name })}
          </div>
        )}
        {target.expert_signed_awaiting_admin && messages.awaitingAdmin && (
          <div className={classes.status}>
            {t(messages.awaitingAdmin, {
              name: target.admin_signatory_full_name,
            })}
          </div>
        )}
        {canLaunch && messages.launchLabel && (
          <div className={classes.signRow}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={signing}
              onClick={() => setLaunchModalOpen(true)}
            >
              {t(messages.launchLabel)}
            </Button>
            <ContractSignatureLaunchModal
              open={launchModalOpen}
              contract={{
                ...target,
                signature_mode: launchSignatureMode || target.signature_mode,
              }}
              loading={signing}
              handleClose={() => setLaunchModalOpen(false)}
              handleConfirm={() => {
                setLaunchModalOpen(false);
                // "Automatique": Yousign signs for the admin without sending
                // them a code, their number isn't checked
                if (
                  (launchSignatureMode || target.signature_mode) === 'automatic'
                ) {
                  launchSignature('');
                } else {
                  openPhoneModal();
                }
              }}
            />
            {messages.phoneModalTitle && messages.phoneModalDescription && (
              <PhoneConfirmationModal
                open={phoneModalOpen}
                title={messages.phoneModalTitle}
                description={messages.phoneModalDescription}
                initialPhone={adminPhone}
                loading={signing}
                handleClose={() => setPhoneModalOpen(false)}
                handleUpdatePhone={updateAdminPhone}
                handleConfirm={launchSignature}
              />
            )}
          </div>
        )}
        {canResend && messages.resendLabel && (
          <div className={classes.signRow}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={signing}
              onClick={resendSignatureLink}
            >
              {t(messages.resendLabel)}
            </Button>
          </div>
        )}
        {canSign && messages.signLabel && (
          <div className={classes.signRow}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={signing}
              onClick={signAsAdmin}
            >
              {signing ? <CircularProgress size={18} /> : t(messages.signLabel)}
            </Button>
          </div>
        )}
      </>
    );

    return (
      <>
        {showDocument && documentPart}
        {showSignature && signaturePart}
      </>
    );
  };

  Extension.displayName = `withExtension(PdfDropzone[${attachmentKind}])`;

  return Extension;
}

export default withPdfDropzone;
